package top.wcpe.allincore.ui.web.preview

import kotlin.js.Promise

/** 管理 PreviewApp 异步候选加载的时序与所有权。 */
internal class PreviewAppLoadController(
    private val source: PreviewCandidateSource,
    private val coordinator: PreviewAppCandidateCoordinator,
    private val showDiagnostics: (List<String>) -> Unit,
) {
    private var generation = 0L
    private var activePath: String? = null
    private var disposed = false

    fun load(path: String): Promise<Unit> {
        val request = beginRequest(path) ?: return Promise.resolve(Unit)
        val loading = try {
            source.load(path)
        } catch (_: Throwable) {
            reportUnexpected(request)
            return Promise.resolve(Unit)
        }
        return loading.then { result -> consume(request, result) }
            .catch { reportUnexpected(request) }
    }

    fun dispose() {
        if (disposed) return
        disposed = true
        generation += 1
        activePath = null
    }

    private fun beginRequest(path: String): Request? {
        if (disposed) return null
        generation += 1
        activePath = path
        return Request(generation, path)
    }

    private fun consume(request: Request, result: PreviewCandidateLoadResult) {
        when (result) {
            is PreviewCandidateLoadResult.Success -> consumeSuccess(request, result)
            is PreviewCandidateLoadResult.Failure -> {
                if (isCurrent(request)) report(result.diagnostics)
            }
        }
    }

    private fun consumeSuccess(request: Request, loaded: PreviewCandidateLoadResult.Success) {
        if (!isCurrent(request)) {
            discardSafely(loaded.ownership)
            return
        }
        try {
            coordinator.commit(coordinator.prepare(loaded))
        } catch (_: Throwable) {
            reportUnexpected(request)
        }
    }

    private fun isCurrent(request: Request): Boolean =
        !disposed && request.generation == generation && request.path == activePath

    private fun reportUnexpected(request: Request) {
        if (isCurrent(request)) report(listOf(SAFE_FAILURE_DIAGNOSTIC))
    }

    private fun report(diagnostics: List<String>) {
        runCatching { showDiagnostics(diagnostics) }
    }

    private fun discardSafely(ownership: PreviewCandidateOwnership) {
        runCatching { ownership.discard() }
    }

    private data class Request(val generation: Long, val path: String)

    private companion object {
        const val SAFE_FAILURE_DIAGNOSTIC = "预览候选处理失败，请检查输入与资源后重试"
    }
}
